const uuid_v4       = require('uuid').v4
const validatorLib 	= require('validator')				// https://www.npmjs.com/package/validator

const utils 		= require('../lib/utils')
const appConfig 	= require('../lib/config').getConfig()

const validationData = {
	validators: {},
	ruleSets: 	{}
}

const helpers = {

	/**
	 * this function is used to prepare ruleSets or rules.
	 *
	 * @param 	rules					object - rule or ruleSet
	 * 									RuleSet-Format:
	 * 										{
	 * 											type: 	<string: and || or || cascade>,
	 * 											rules: 	[<rule>, ...]
	 * 										}
	 * 									Rule-Format:
	 * 										{
     * 											"input": <input>,
     * 											"validator": <validatorCode> (e.g., "minLength", "required", ...),
     * 											"parameters": {
     * 												<parameterName>: <parameterValue> (e.g., min: 3)
     * 											},
     * 											"options": {
     * 												<optionName>: <optionValue> (e.g., sanitizePossible: true)
     * 											}
	 * 										}
	 * 									Input-Format:
	 * 										<string> - one single fieldCode
	 * 										||
	 * 										{
     * 											"type": <string: sum | product | join | count>,
     * 											"fieldCodes": [<string: fieldCode>, ...],
     * 											"options": {
     * 												<optionName>: <optionValue> (e.g., delimiter: ";")
     * 											}
	 * 										}
	 *
	 * @param 	affectedFieldCodes		object - affected fieldCodes of this rule or ruleSet
	 * 									NOTE: this flag is used for internal workflow only and must not be used or set outside this function
	 * @param 	subRuleSetParent		object - preparedRules Object of a rule whichs' validator contains a subRuleSet.
	 * 									NOTE: 	if this flag is defined the ruleSets and rules has to be prepared in the context of a validators ruleSet instead of a normal ruleSet.
	 * 											this is especially important when parameters and options has to be prepared as mapping instead of key-value-stores.
	 * @return 	mixed - prepared rule or ruleSet or false in an error case
	 */
	prepareRules: function(rules, affectedFieldCodes, subRuleSetParent){
		// init
		var preparedRules = false;

		// differ between ruleSets and rules
		switch((ruleSetType = rules.type && rules.rules ? rules.type : 'rule')){
			case 'and':
			case 'or':
			case 'cascade':
				// prepare ruleSet
				preparedRules = {
					affectedFieldCodes: {},
					type: 				ruleSetType,
					rules: 				[]
				};
				var r;
				for(r in rules.rules){
					if((preparedRules.rules[r] = helpers.prepareRules(rules.rules[r], preparedRules.affectedFieldCodes, subRuleSetParent)) === false) return false;
				}
				break;

			case 'rule':
			default:
				// prepare rule
				var input;

				// check if validator and inputDefinition is available
				if(rules.validator && validationData.validators[rules.validator] && (input = ((subRuleSetParent && subRuleSetParent.input ? subRuleSetParent.input : false) || rules.input))){
					var f;

					// this is a rule within a subRuleSet of a validator
					if(subRuleSetParent){
						var p, o;
						// resolve parameter- and options-mapping
						for(p in rules.parameters){
							rules.parameters[p] = subRuleSetParent.parameters[rules.parameters[p]];
						}
						for(o in rules.options){
							rules.options[o] = subRuleSetParent.options[rules.options[o]];
						}
					}

					preparedRules = {
						affectedFieldCodes: {},
						validator: 			validationData.validators[rules.validator],
						parameters: 		rules.parameters || {},
						options: 			rules.options || {},
						input: 				input
					};

					if(typeof(input) === 'string'){
						preparedRules.affectedFieldCodes[input] = true;
					}
					else if(utils.isCollection(input) && input.fieldCodes){
						for(f in input.fieldCodes){
							preparedRules.affectedFieldCodes[input.fieldCodes[f]] = true;
						}
					}
					else {
						preparedRules = false;
					}

					// handle subValidators if necessary
					if(preparedRules && preparedRules.validator.ruleSet && (preparedRules.ruleSet = helpers.prepareRules(utils.simpleCopy(preparedRules.validator.ruleSet), undefined, preparedRules)) === false){
						preparedRules = false;
					}

				}
				if(!preparedRules){
					console.error('rule preparation failed for validator: ' + rules.validator + ' @ input: ' + rules.input);
				}
				break;
		}

		if(preparedRules && affectedFieldCodes){
			utils.refMerge(affectedFieldCodes, preparedRules.affectedFieldCodes);
		}

		return preparedRules;
	},

	/**
	 * this function checks if the conditions given by the rules parameter are met or not.
	 *
	 * @param 	rules							object - ruleSet | rule (format documented at helpers.prepareRules method)
	 * @param 	fieldCodes						object - list of fieldCodes which should be checked. (fieldCodes are the keys of the list and the value has to be true)
	 * @param 	resourceAttributes				object - list of resourceAttributes which should be used as inputs. (fieldCodes are the keys of the list)
	 * @param 	validationResult				object - contains the result of the following form
	 * 											{
	 * 												errors: {
	 * 													<fieldCode>: <errorMessage>,
	 * 													...
	 * 												},
	 * 												testedFieldCodes: {
	 * 													<fieldCode>: true,
	 * 													...
	 * 												},
	 * 											}
	 * @param 	langCode						string - actual users' langCode
	 * @param 	additionalFieldData				object - list of additionalFieldData which should be provided to error message replacement. (fieldCodes are the keys of the list)
	 * 											NOTE: the additionalFieldData will be provided by the name "field" within the error message.
	 * 											NOTE: if the additionalData of a field contains a "lang"-node it will be reduced to the value given at "lang".<langCode>
	 * @params 	preventValidationFieldCodes		object - list of all fieldCodes (<fieldCode> => true) which should not be validated.
	 * @param 	withinSubRuleSet				boolean - this flag is set to true if the validation routine is within a subRuleSet (subValidator).
	 * 											NOTE: this flag must not be set from outside and is used for internal purposes only.
	 * @return 	boolean - valid or not
	 */
	validateRules: function(rules, fieldCodes, resourceAttributes, validationResult, langCode, additionalFieldData, preventValidationFieldCodes, withinSubRuleSet){
		// init
		var ruleSetType;

		// check if validation is necessary
		var affectedFieldCode;
		var rulesValidationNecessary = false;
		for(affectedFieldCode in rules.affectedFieldCodes){
			if(fieldCodes[affectedFieldCode] === true){
				rulesValidationNecessary = true;
				break;
			}
		}

		if(rulesValidationNecessary){

			// differ between ruleSets and rules
			switch((ruleSetType = rules.type && rules.rules ? rules.type : 'rule')){
				case 'and':
				case 'or':
				case 'cascade':
					// prepare ruleSet
					var r;
					var setValidationResult = {
						errors: {},
						testedFieldCodes: {}
					};
					var validationResultFound = {
						positive: false,
						negative: false
					};
					var setFieldCodes;
					if(ruleSetType === 'or') 	setFieldCodes = rules.affectedFieldCodes;
					else 						setFieldCodes = fieldCodes;

					for(r in rules.rules){
						// negative validation result
						if(helpers.validateRules(rules.rules[r], setFieldCodes, resourceAttributes, setValidationResult, langCode, additionalFieldData, preventValidationFieldCodes, withinSubRuleSet) === false){
							validationResultFound.negative = true;
							if(ruleSetType === 'cascade'){
								break;
							}
						}
						// positive validation result
						else {
							validationResultFound.positive = true;
							if(ruleSetType === 'or'){
								break;
							}
						}
					}

					utils.refMerge(validationResult.testedFieldCodes, setValidationResult.testedFieldCodes);

					if(ruleSetType === 'or'){
						// sanitize tested field list
						for(affectedFieldCode in rules.affectedFieldCodes){
							if(!validationResult.testedFieldCodes[affectedFieldCode]){
								validationResult.testedFieldCodes[affectedFieldCode] = true;
							}
						}
					}

					// errors in charge?
					if(
						(
							!validationResultFound.positive
							&&
							ruleSetType === 'or'
						)
						||
						(
							validationResultFound.negative
							&&
							(
								ruleSetType === 'and'
								||
								ruleSetType === 'cascade'
							)
						)
					){
						// TODO: handle multiple errors on equal fieldCodes in case of OR-RuleSets
						utils.refMerge(validationResult.errors, setValidationResult.errors);
						return false;
					}
					break;

				case 'rule':
				default:

					// sanitize tested field list
					for(affectedFieldCode in rules.affectedFieldCodes){
						if(!validationResult.testedFieldCodes[affectedFieldCode]){
							validationResult.testedFieldCodes[affectedFieldCode] = true;
						}
					}

					// is this a ruleSet-rule ...
					var valid;
					if(rules.ruleSet){
						valid = helpers.validateRules(rules.ruleSet, fieldCodes, resourceAttributes, validationResult, langCode, additionalFieldData, preventValidationFieldCodes, true);
					}
					// ... or a primitive one?
					else {
						// prevent validation of fieldCodes if necessary
						if(preventValidationFieldCodes[rules.input]) {
							valid = true
						} else {
							valid = validator[rules.validator.code](
								helpers.collectInput(
									rules.input,
									resourceAttributes
								),
								rules.parameters,
								rules.options
							);
						}
					}

					// handle errors only if this is the real validator in charge
					// NOTE: this means if the rule is within a ruleSet of another validator, the error message has to be defined by this validator.
					if(!valid && !withinSubRuleSet){
						for(affectedFieldCode in rules.affectedFieldCodes){
							// init validation error
							if(!validationResult.errors[affectedFieldCode]) 	validationResult.errors[affectedFieldCode] = '';
							else 												validationResult.errors[affectedFieldCode] += "<br>";

							// set validation error message
							if(!rules.validator.messages || !rules.validator.messages[langCode]){
								validationResult.errors[affectedFieldCode] += ' ## VALIDATION MESSAGE UNDEFINED ## ';
							}
							else {
								var field = {};
								if(additionalFieldData[affectedFieldCode]){
									field = utils.simpleCopy(additionalFieldData[affectedFieldCode]);

									if(additionalFieldData[affectedFieldCode].lang && additionalFieldData[affectedFieldCode].lang[langCode]){
										field.lang = additionalFieldData[affectedFieldCode].lang[langCode];
									}
								}

								// @see MARK: TRANSLATE_ALREADY_TRANSLATED_MESSAGE_FOR_REPLACEMENTS_ONLY
								validationResult.errors[affectedFieldCode] += storage.__(rules.validator.messages[langCode], {
									fieldCode: 	affectedFieldCode,
									field: 		field,
									rule: 		rules,
									_preventMissingKeyLog: true
								});
							}
						}
					}
					return valid;
					break;
			}
		}

		return true;
	},

	/**
	 * this function is used to collect the input data according to the given input definition.
	 *
	 * @param 	input					string || {} - input definition (usually just a fieldCode or a complex definition documented at helpers.prepareRules function)
	 * @param 	resourceAttributes		object - actual resourceAttributes
	 * @return 	mixed - input data to check the rule against
	 */
	collectInput: function(input, resourceAttributes){
		if(!resourceAttributes) 		return undefined;
		if(typeof(input) === 'string') 	return resourceAttributes[input];

		// TODO: IMPLEMENT COLLECTION functionality
		return '';
	}
}

const validator = {

	// INTERNAL VALIDATORS
	min: 		function(input, parameters, options){
		return (input * 1) >= parameters.min;
	},
	max: 		function(input, parameters, options){
		return (input * 1) <= parameters.max;
	},
	minLength: 	function(input, parameters, options){
		return (input + '').length >= parameters.min;
	},
	maxLength: 	function(input, parameters, options){
		return (input + '').length <= parameters.max;
	},
	required: 	function(input, parameters, options){
		return (
			input !== undefined
			&&
			(
				options.emptyTypesAllowed
				||
				(
					input !== null
					&&
					input !== 0
					&&
					input !== ''
					&&
					input !== '0'
				)
			)
		);
	},
	// check if fec-media field is set.
	// media fields may have dirty assignments which are marked to be deleted which must not count as filled assignment.
	requiredMedia: function(input, parameters, options){
		if(
			input !== undefined
			&&
			input !== null
			&&
			typeof input === 'object'
		){
			for(const media of Object.values(input || {})){
				if(!media.delete){
					return true
				}
			}
		}

		return false
	},
	isEmpty: 	function(input, parameters, options){
		return !this.required(input, parameters, options);
	},
	equals: 	function(input, parameters, options){
		return parameters && parameters.value !== undefined && input == parameters.value;
	},

	// EXTERNAL VALIDATORS
	isAlpha: 		function(input, parameters, options){
		return validatorLib.isAlpha(input + '', parameters.locale || appConfig.validator.defaultLocale);
	},
	isAlphanumeric: function(input, parameters, options){
		return validatorLib.isAlphanumeric(input + '', parameters.locale || appConfig.validator.defaultLocale);
	},
	isAscii: 		function(input, parameters, options){
		return validatorLib.isAscii(input + '');
	},
	isBase64: 		function(input, parameters, options){
		return validatorLib.isBase64(input + '');
	},
	isCreditCard: 	function(input, parameters, options){
		return validatorLib.isCreditCard(input + '');
	},
	isCurrency: 	function(input, parameters, options){
		return validatorLib.isCurrency(input + '', options);
	},
	isDataURI: 		function(input, parameters, options){
		return validatorLib.isDataURI(input + '');
	},
	isDecimal: 		function(input, parameters, options){
		return validatorLib.isDecimal(input + '');
	},
	isEmail: 		function(input, parameters, options){
		// TODO: normalizeEmail - sanitizing?
		return validatorLib.isEmail(input + '', options);
	},
	isFQDN: 		function(input, parameters, options){
		return validatorLib.isFQDN(input + '', options);
	},
	isFloat: 		function(input, parameters, options){
		return validatorLib.isFloat(input + '', options);
	},
	isInt: 			function(input, parameters, options){
		return validatorLib.isInt(input + '', parameters);
	},
	isIP: 			function(input, parameters, options){
		return validatorLib.isIP(input + '', options.version || appConfig.validator.defaultIPVersion);
	},
	isISBN: 		function(input, parameters, options){
		return validatorLib.isISBN(input + '', options.version || appConfig.validator.defaultISBNVersion);
	},
	isISSN: 		function(input, parameters, options){
		return validatorLib.isISSN(input + '', options);
	},
	isISO8601: 		function(input, parameters, options){
		return validatorLib.isISO8601(input + '');
	},
	isLength: 		function(input, parameters, options){
		return validatorLib.isLength(input + '', {
			min: parameters.min || 0,
			max: parameters.max || undefined
		});
	},
	isMACAddress: 	function(input, parameters, options){
		return validatorLib.isMACAddress(input + '');
	},
	isMD5: 			function(input, parameters, options){
		return validatorLib.isMD5(input + '');
	},
	isMobilePhone: 	function(input, parameters, options){
		return validatorLib.isMobilePhone(input + '', parameters.locale || appConfig.validator.defaultLocale);
	},
	isURL: 			function(input, parameters, options){
		return validatorLib.isURL(input + '', options);
	},
	isUUID: 		function(input, parameters, options){
		return validatorLib.isUUID(input + '', parameters.version || appConfig.validator.defaultUUIDVersion);
	},
	matches: 		function(input, parameters, options){
		return validatorLib.matches(input + '', parameters.pattern, parameters.modifiers);
	}
};

const validationPromoter = {

	/**
	 * this function initializes the list of validators.
	 * NOTE: this function overwrites the internal list of validators and should therefor only be called exactly once.
	 *
	 * @param 	validators		array - flat list of validators:
	 */
	initValidators: function(validators){
		if(validators){
			if(!validatorLib) return;

			var v;
			for(v in validators){
				if(!validators[v].code || (typeof(validator[validators[v].code]) != 'function' && !validators[v].ruleSet)){
					// TODO: only display console errors in dev mode
					//console.error('unknown validatorCode found: ' + validators[v].code);
					validators[v] = undefined;
				}
			}

			validationData.validators = validators;
		}
	},

	/**
	 * this function is used to prepare the given ruleSet.
	 * NOTE: 	this function can be called more than once per ruleSetID
	 * 			but will take no effect if the ruleSet identified
	 * 			by the given ruleSetID has been initialized before.
	 *
	 * @param 	ruleSetID		string | int - optional - ruleSetID to identify the ruleSet later.
	 * 							NOTE: if no ruleSetID is defined a uuid will be generated which will be returned by the function.
	 * @param 	ruleSet			object - ruleSet of the form described in the helpers.prepareRules method.
	 * @return 	string | int | false - ruleSetID (the given one or a generated one) or false in an error case
	 */
	initRules: function(ruleSetID, ruleSet){
		if(!validatorLib) return false;

		// preconditions not met
		if(!ruleSet) return false;
		if(ruleSetID == undefined) ruleSetID = uuid_v4();

		// no init necessary
		if(validationData.ruleSets[ruleSetID]) return ruleSetID;

		// init ruleSet
		var preparedRuleSet;
		if((preparedRuleSet = helpers.prepareRules(ruleSet)) !== false){
			validationData.ruleSets[ruleSetID] = preparedRuleSet;
			return ruleSetID;
		}

		return false;
	},

	/**
	 * this function returns a list of all affected fieldCodes (list-keys: true) according to the given ruleSetID.
	 *
	 * @param 	ruleSetID		string || int - ruleSetID
	 * @return  {} - list of fieldCodes of the following form
	 * 			{
	 * 				<fieldCode>: true
	 * 			}
	 */
	getAffectedFieldCodes: function(ruleSetID){
		if(ruleSetID){
			if(!validatorLib){
				console.error('validatorLib has not been loaded! check webpack.config if this is a problem.')
				return {}
			}

			if(validationData.ruleSets[ruleSetID] && validationData.ruleSets[ruleSetID].affectedFieldCodes) return validationData.ruleSets[ruleSetID].affectedFieldCodes;
		}

		return {};
	},

	/**
	 * check if the given resourceAttributes meet all conditions defined by the ruleSet identified by the given ruleSetID.
	 * NOTE: if the fieldCodes parameter is defined only the given fieldCodes will be checked.
	 *
	 * @param 	ruleSetID						string | int - ruleSetID
	 * @param 	fieldCodes						object - optional - list of fieldCodes to check
	 * 											NOTE: if empty all fieldCodes will be checked.
	 * @param 	resourceAttributes				object - flat list of attributes to check against.
	 * 											NOTE: the keys of this list has to be equal the corresponding fieldCodes used within the ruleSet and the potentially given fieldCodes-list entries
	 * @param 	langCode						string - langCode which should be used for error messages (default: de-at)
	 * @params 	additionalFieldData				object - list of all fieldCodes associated with its additional data
	 * @params 	preventValidationFieldCodes		object - list of all fieldCodes (<fieldCode> => true) which should not be validated.
	 * @return 	{
	 * 				invalid: <boolean> - validation OK or not,
	 * 				fields: { - list of validationErrors or tested fieldCodes
	 * 					<fieldCode>: <true || string: errorMessage>,
	 * 					...
	 * 				}
	 * 			}
	 */
	validate: function(ruleSetID, fieldCodes, resourceAttributes, langCode, additionalFieldData, preventValidationFieldCodes){
		// check preconditions
		if(!ruleSetID || !validationData.ruleSets[ruleSetID] || !resourceAttributes) return true;

		// set fieldCodes if necessary
		if(!fieldCodes) fieldCodes = validationData.ruleSets[ruleSetID].affectedFieldCodes;

		preventValidationFieldCodes = preventValidationFieldCodes || {}

		if(!validatorLib){
			console.error('validatorLib has not been loaded! check webpack.config if this is a problem.')
			return {}
		}

		var validationResult = {
			errors: {},
			testedFieldCodes: {}
		};
		if(helpers.validateRules(validationData.ruleSets[ruleSetID], fieldCodes, resourceAttributes, validationResult, langCode || ((appConfig || {}).language || {}).default || 'de-at', additionalFieldData, preventValidationFieldCodes)){
			return {
				invalid: 	false,
				fields: 	validationResult.testedFieldCodes
			};
		}
		return {
			invalid: 	true,
			fields: 	validationResult.errors
		};
	}

}

module.exports = validationPromoter
